<script setup>
/**
 * 「充值记录」页，逐条对齐旧版 web/22.fe50f3bdc776afdb89ae.js 里的
 * app-recharge 组件（类 g、模板工厂 x + 子模板 _/d/f/C/O/M/P/k/v、样式 m）。
 *
 * 旧版类 g：
 *   字段：startTime=''、endTime=''、state='-2'、paramShow=true、page
 *   ngOnInit → getLogList()
 *   getLogList(n): POST /recharge/lists {page, start, end, state}
 *     lists = page_index === 1 ? data.lists : lists.concat(data.lists)
 *   deleteLog(row, index): alert{header:'确认', message:'确定取消该申请吗？'}
 *     确认后 POST /recharge/delete {recharge: recharge_id}
 *     成功 → toast「取消成功」+ lists.splice(index, 1)
 *   changParam(): page_index = 1; getLogList()
 *   doRefresh / doInfinite 与其它列表页一致
 *
 * 跟流水明细一样，startTime / endTime 始终传空（模板里只有「状态」一个筛选）。
 *
 * 明细行结构（子模板 P）：
 *   div.log-list-item
 *     div.log-info    span.title(bank_title) / span.username(bank_username) / span.time(recharge_create_time)
 *     div.log-money   span.money(recharge_money)
 *                     *ngIf="give_money>0" → span.give-money「赠送：x」+ span.give-reason
 *     div.log-state   handle_state==1 → div.done「已处理」
 *                     handle_state==0 → div.undone「处理中」+ div.delete > 红色小按钮「取消」
 */
import { computed, onMounted, ref } from 'vue'
import { request } from '../services/api'
import PullRefresher from '../components/PullRefresher.vue'
import LegacySelect from '../components/LegacySelect.vue'
import LegacyAlert from '../components/LegacyAlert.vue'

const paramShow = ref(true)
// 旧版 state 默认字符串 '-2'（不限）
const state = ref('-2')
const lists = ref(null)
const page = ref({ item_total: 0, page_index: 1, page_size: 15, page_count: 0 })
const loading = ref(false)
const notice = ref('')
const confirmTarget = ref(null)

// 旧版这两个参数始终为空
const startTime = ref('')
const endTime = ref('')

const hasMore = computed(() => page.value.page_count > 1 && page.value.page_count != page.value.page_index)

// 旧版三个 ion-select-option
const stateOptions = [
  { value: '-2', label: '不限' },
  { value: '1', label: '已处理' },
  { value: '0', label: '未处理' },
]

// 旧版 DecimalPipe '.2-2'
const decimal2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function fmt2(value) { return decimal2.format(Number(value || 0)) }

/** 旧版 getLogList(n=null) */
async function getLogList(event = null) {
  loading.value = true
  try {
    const response = await request('/recharge/lists', {
      method: 'POST',
      data: { page: page.value.page_index, start: startTime.value, end: endTime.value, state: state.value },
    })
    page.value = { ...page.value, ...(response.page || {}) }
    const incoming = response.data?.lists || []
    lists.value = page.value.page_index === 1 ? incoming : [...(lists.value || []), ...incoming]
  } catch (error) {
    notice.value = error.message || '加载失败'
  } finally {
    loading.value = false
    event?.complete()
  }
}

/** 旧版 changParam() */
function changParam() {
  page.value = { ...page.value, page_index: 1 }
  void getLogList()
}

/** 旧版 doRefresh(n) */
function doRefresh(event) {
  page.value = { ...page.value, page_index: 1 }
  void getLogList(event)
}

/** 旧版 doInfinite(n) */
function doInfinite() {
  const next = page.value.page_index + 1
  if (next > page.value.page_count) { notice.value = '已经最后一页了'; return }
  page.value = { ...page.value, page_index: next }
  void getLogList()
}

/** 旧版 deleteLog(row, index) 的确认 alert */
function askDelete(row, index) { confirmTarget.value = { row, index } }

async function doDelete() {
  const target = confirmTarget.value
  confirmTarget.value = null
  if (!target) return
  try {
    // 后端 Recharge::delete() 读的是 Request::param('recharge')
    await request('/recharge/delete', { method: 'POST', data: { recharge: target.row.recharge_id } })
    notice.value = '取消成功'
    if (target.index >= 0 && lists.value) lists.value.splice(target.index, 1)
  } catch (error) {
    notice.value = error.message || '取消失败'
  }
}

// 旧版 ngOnInit
onMounted(() => { void getLogList() })
</script>

<template>
  <div class="record-page">
    <!-- 旧版 ion-header > ion-toolbar，返回按钮 routerLink="/tabs/user" -->
    <header class="modal-header">
      <RouterLink class="modal-back" to="/user/profile" aria-label="返回">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1 class="modal-title">充值记录</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <PullRefresher
      pulling-text="下拉刷新"
      refreshing-text="下拉刷新..."
      refreshing-spinner="circles"
      @refresh="doRefresh"
    >
      <!-- 旧版第一个 ion-list：筛选 -->
      <section class="ion-list">
        <div class="list-header" @click="paramShow = !paramShow">
          <span> 筛选 </span>
          <svg v-if="paramShow" class="header-arrow" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 392L100 236l28-28 128 128 128-128 28 28z" fill="currentColor" /></svg>
          <svg v-else class="header-arrow" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 120l156 156-28 28-128-128-128 128-28-28z" fill="currentColor" /></svg>
        </div>

        <!-- 旧版 f：*ngIf="paramShow" -->
        <div v-if="paramShow" class="list-item">
          <div class="list-item-inner">
            <div class="ion-label">状态:</div>
            <LegacySelect
              v-model="state"
              :options="stateOptions"
              alert-header="状态:"
              cancel-text="取消"
              ok-text="确定"
              @change="changParam"
            />
          </div>
        </div>
      </section>

      <!-- 旧版 k：*ngIf="lists" -->
      <div v-if="lists" class="log-list">
        <div v-for="(row, index) in lists" :key="row.recharge_id ?? index" class="log-list-item">
          <div class="log-info">
            <span class="title"> {{ row.bank_title }} </span>
            <span class="username"> {{ row.bank_username }} </span>
            <span class="time"> {{ row.recharge_create_time }} </span>
          </div>
          <div class="log-money">
            <span class="money"> {{ fmt2(row.recharge_money) }} </span>
            <!-- 旧版 C：*ngIf="give_money > 0" -->
            <template v-if="Number(row.give_money) > 0">
              <span class="give-money"> 赠送：{{ fmt2(row.give_money) }} </span>
              <span class="give-reason"> {{ row.give_reason }} </span>
            </template>
          </div>
          <div class="log-state">
            <!-- 旧版 O：handle_state == 1 -->
            <div v-if="row.handle_state == 1" class="done"> 已处理 </div>
            <!-- 旧版 M：handle_state == 0 -->
            <template v-else-if="row.handle_state == 0">
              <div class="undone"> 处理中 </div>
              <div class="delete">
                <button class="ion-button button-small button-solid ion-color-danger" type="button" @click="askDelete(row, index)">
                  <span class="button-inner">取消</span>
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- 旧版 v：ion-infinite-scroll，loadingSpinner="bubbles" loadingText="正在加载" -->
      <button v-if="hasMore" class="load-more" type="button" :disabled="loading" @click="doInfinite">
        正在加载
      </button>
      <p v-if="notice" class="modal-notice">{{ notice }}</p>
    </PullRefresher>

    <!-- 旧版 deleteLog 的确认 alert -->
    <Teleport to="body">
      <LegacyAlert
        v-if="confirmTarget"
        header="确认"
        message="确定取消该申请吗？"
        cancel-text="取消"
        ok-text="确认"
        @cancel="confirmTarget = null"
        @confirm="doDelete"
      />
    </Teleport>
  </div>
</template>

<style scoped>
/* 页面外壳与顶栏跟其它旧版还原页保持一致 */
.record-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0;text-decoration:none}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
.list-header{display:flex;align-items:center;justify-content:space-between;width:100%;margin:0;padding:0 16px;min-height:45px;font-size:14px;background:transparent;color:#000;overflow:hidden;cursor:pointer}
.list-header .header-arrow{width:1em;height:1em;flex:none;opacity:.54}
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;justify-content:space-between;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}
.ion-label{display:block;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}

/* --- 以下逐条照抄旧版样式表 m（chunk 22），:host → .record-page --- */
.log-list .log-list-item{width:100%;display:flex;align-items:center;justify-content:space-between;font-size:.8rem;padding:10px;border-bottom:1px solid #ccc;line-height:2}
.log-list .log-list-item span{display:block}
.log-list .log-list-item .log-info{width:40%}
.log-list .log-list-item .log-info .title{color:#000;font-size:.8rem}
.log-list .log-list-item .log-info .username{color:#666;font-size:.8rem}
.log-list .log-list-item .log-info .time{color:#ccc;font-size:.8rem}
.log-list .log-list-item .log-money{width:40%;text-align:right}
.log-list .log-list-item .log-money .money{font-size:1.2rem;color:red}
.log-list .log-list-item .log-money .give-money{color:#ccc}
.log-list .log-list-item .log-money .give-reason{color:#ccc}
.log-list .log-list-item .log-state{width:20%;text-align:center}
.log-list .log-list-item .log-state div,.log-list .log-list-item .log-state span{text-align:center}
.log-list .log-list-item .log-state .done{color:green}
.log-list .log-list-item .log-state .undone{color:red}

/* ion-button color="danger" size="small"，取自 web/75（md）
   .button-small: padding 0 .9em / height 2.1em / font-size 13px；danger → #f04141 */
.ion-button{
  display:inline-flex;align-items:center;justify-content:center;
  margin:4px 2px;border:0;border-radius:4px;
  font-weight:500;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;
  height:2.1em;font-size:13px;padding:0 .9em;
  background:var(--ion-color-danger,#f04141);color:#fff;
  box-shadow:0 3px 1px -2px rgb(0 0 0 / 20%),0 2px 2px 0 rgb(0 0 0 / 14%),0 1px 5px 0 rgb(0 0 0 / 12%);
}
.ion-button .button-inner{display:flex;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:center;width:100%;height:100%}

.load-more{display:block;width:calc(100% - 32px);margin:10px 16px;padding:8px 0;border:1px solid #ddd;border-radius:4px;background:#fafafa;font-size:.8rem}
.modal-notice{margin:10px 16px;color:#999;font-size:.8rem;text-align:center}
</style>
